import React, { useEffect, useMemo, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

const PAYMENT_DETAILS_ERROR_MESSAGE = 'Zahlungsdaten konnten nicht geprüft werden. Bitte prüfe deine Angaben.';
const READY_FIELD_NAMES = ['cardNumber', 'cardExpiry', 'cardCvc'];

const CARD_ELEMENT_STYLE = {
  base: {
    color: '#30313d',
    fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    '::placeholder': {
      color: '#8a8f98',
    },
  },
  invalid: {
    color: '#df1b41',
  },
};

export const PreparedStripePaymentSection = ({
  classes,
  buttonClassName,
  paymentFlow,
  paymentProcessing,
  loading,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [readyFields, setReadyFields] = useState({});
  const [paymentElementError, setPaymentElementError] = useState(null);
  const [expressCheckoutAvailable, setExpressCheckoutAvailable] = useState(null);

  const expressCheckoutOptions = useMemo(() => ({
    buttonHeight: 54,
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black',
    },
    buttonType: {
      applePay: 'book',
      googlePay: 'book',
    },
    layout: {
      maxColumns: 1,
      overflow: 'never',
    },
    paymentMethods: {
      amazonPay: 'never',
      applePay: 'always',
      googlePay: 'always',
      klarna: 'never',
      link: 'never',
      paypal: 'never',
    },
  }), []);

  const cardNumberOptions = useMemo(() => ({
    style: CARD_ELEMENT_STYLE,
    placeholder: '1234 1234 1234 1234',
    showIcon: true,
    iconStyle: 'default',
    disableLink: true,
  }), []);

  const cardExpiryOptions = useMemo(() => ({
    style: CARD_ELEMENT_STYLE,
    placeholder: 'MM/JJ',
  }), []);

  const cardCvcOptions = useMemo(() => ({
    style: CARD_ELEMENT_STYLE,
    placeholder: 'Prüfziffer',
  }), []);

  useEffect(() => {
    paymentFlow.resumePreparedPayment({ stripe });
  }, [paymentFlow, stripe]);

  useEffect(() => {
    setPaymentElementReady(READY_FIELD_NAMES.every((fieldName) => readyFields[fieldName]));
  }, [readyFields]);

  const markFieldReady = (fieldName) => {
    setReadyFields((previousFields) => {
      if (previousFields[fieldName]) return previousFields;
      return {
        ...previousFields,
        [fieldName]: true,
      };
    });
  };

  const handleCardFieldChange = (event) => {
    setPaymentElementError(event?.error?.message || null);
  };

  const focusStripeElement = (ElementComponent) => {
    const nextElement = elements?.getElement(ElementComponent);
    if (typeof nextElement?.focus === 'function') {
      nextElement.focus();
    }
  };

  const handleCardNumberChange = (event) => {
    handleCardFieldChange(event);
    if (event?.complete && !event?.error) {
      focusStripeElement(CardExpiryElement);
    }
  };

  const handleCardExpiryChange = (event) => {
    handleCardFieldChange(event);
    if (event?.complete && !event?.error) {
      focusStripeElement(CardCvcElement);
    }
  };

  const handleExpressCheckoutReady = (event) => {
    const availableMethods = event?.availablePaymentMethods;
    setExpressCheckoutAvailable(Boolean(
      availableMethods
      && Object.values(availableMethods).some(Boolean)
    ));
  };

  const handleExpressCheckoutConfirm = (event) => {
    return paymentFlow.confirmPreparedPayment({
      stripe,
      elements,
      submitBeforeConfirm: true,
      expressEvent: event,
    });
  };

  const handleCardPayment = () => {
    const cardElement = elements?.getElement(CardNumberElement);

    paymentFlow.confirmPreparedPayment({
      stripe,
      elements,
      cardElement,
      requirePaymentElementReady: true,
      paymentElementReady,
      paymentElementLoadError: paymentElementError,
    });
  };

  return (
    <div className={classes.stripeSection}>
      <div
        className={[
          classes.expressCheckoutWrapper,
          expressCheckoutAvailable === false ? classes.expressCheckoutHidden : '',
        ].filter(Boolean).join(' ')}
      >
        <ExpressCheckoutElement
          options={expressCheckoutOptions}
          onReady={handleExpressCheckoutReady}
          onLoadError={() => setExpressCheckoutAvailable(false)}
          onConfirm={handleExpressCheckoutConfirm}
        />
      </div>
      <div className={classes.stripePaymentElementWrapper}>
        {!paymentElementReady && !paymentElementError && (
          <div className={`${classes.stripeLoadingMessage} slimRegularSmallText`}>
            Zahlungsformular wird geladen...
          </div>
        )}
        <div className={classes.stripeCardFieldsWrapper}>
          <div className={classes.stripeCardFieldGroup}>
            <label className={`${classes.stripeCardLabel} slimRegularSmallText`}>Kartennummer</label>
            <div className={classes.stripeCardInput}>
              <CardNumberElement
                options={cardNumberOptions}
                onReady={() => markFieldReady('cardNumber')}
                onChange={handleCardNumberChange}
              />
            </div>
          </div>
          <div className={classes.stripeCardFieldsRow}>
            <div className={classes.stripeCardFieldGroup}>
              <label className={`${classes.stripeCardLabel} slimRegularSmallText`}>Ablaufdatum</label>
              <div className={classes.stripeCardInput}>
                <CardExpiryElement
                  options={cardExpiryOptions}
                  onReady={() => markFieldReady('cardExpiry')}
                  onChange={handleCardExpiryChange}
                />
              </div>
            </div>
            <div className={classes.stripeCardFieldGroup}>
              <label className={`${classes.stripeCardLabel} slimRegularSmallText`}>Sicherheitscode</label>
              <div className={classes.stripeCardInput}>
                <CardCvcElement
                  options={cardCvcOptions}
                  onReady={() => markFieldReady('cardCvc')}
                  onChange={handleCardFieldChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {paymentElementError && (
        <div className={classes.errorMessage}>{PAYMENT_DETAILS_ERROR_MESSAGE}</div>
      )}

      <button
        type="button"
        onClick={handleCardPayment}
        className={buttonClassName}
        disabled={!stripe || !paymentElementReady || Boolean(paymentElementError) || paymentProcessing || loading}
      >
        {paymentProcessing
          ? 'Zahlung wird verarbeitet...'
          : paymentElementReady
            ? 'Jetzt bezahlen'
            : 'Zahlungsformular lädt...'}
      </button>
    </div>
  );
};

export default PreparedStripePaymentSection;
